import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Table,
  Card,
  Badge,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaUserPlus,
  FaUserCheck,
  FaDownload,
  FaCopy,
} from "react-icons/fa";

import { callsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;

const InboundCallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [leadTab, setLeadTab] = useState("all"); // all | new | existing
  const [outcome, setOutcome] = useState("all"); // all | qualified | unqualified

  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState({ transcript: '', name: '' });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ------------------------------
  // Fetch calls
  // ------------------------------
  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: PAGE_SIZE,
        search,
      };

      if (outcome !== "all") params.outcome = outcome;
      if (leadTab === "new") params.is_existing_lead = false;
      if (leadTab === "existing") params.is_existing_lead = true;

      const res = await callsAPI.getInboundCalls(params);

      setCalls(res.data.results || []);
      setCount(res.data.count || 0);
      setExpandedId(null);
    } catch {
      toast.error("Failed to load inbound calls");
    } finally {
      setLoading(false);
    }
  }, [page, search, leadTab, outcome]);

  useEffect(() => {
    setLoading(true);
    fetchCalls();
  }, [fetchCalls]);

  const handleExportCSV = async () => {
    try {
      const response = await callsAPI.exportInboundCallsCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inbound_calls.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Inbound calls exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export inbound calls');
    }
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  const badgeVariant = (v = "") =>
    v === "qualified"
      ? "success"
      : v === "unqualified"
      ? "secondary"
      : "dark";

  const proposalBadge = (status) => (
    <Badge bg="info" className="ms-2">
      {status || "Not Set"}
    </Badge>
  );

  if (loading) return <LoadingSpinner text="Loading inbound calls..." />;

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <Container fluid className="page-container">
      {/* HEADER */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <h1 className="page-header-title mb-0 me-3">Inbound Calls</h1>
            <Badge bg="primary" className="fs-6">{count} calls</Badge>
          </div>
          <Button variant="outline-success" onClick={handleExportCSV}>
            <FaDownload className="me-2" />
            Export CSV
          </Button>
        </div>
        <p className="page-header-subtitle text-muted mb-0">
          Analyze inbound calls, lead quality, and proposals
        </p>
      </div>

      {/* SEARCH AND FILTERS */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="p-3">
          {/* SEARCH */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Search Calls</Form.Label>
            <div className="position-relative">
              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Form.Control
                placeholder="Search by name, phone, or issue..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="ps-5"
              />
            </div>
          </Form.Group>

          {/* LEAD TYPE FILTER */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Lead Type</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { key: "all", label: "All Leads", icon: null },
                { key: "new", label: "New Leads", icon: FaUserPlus },
                { key: "existing", label: "Existing Leads", icon: FaUserCheck }
              ].map(({ key, label, icon: Icon }) => (
                <Button
                  key={key}
                  variant={leadTab === key ? "primary" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setPage(1);
                    setLeadTab(key);
                  }}
                  className="d-flex align-items-center"
                >
                  {Icon && <Icon className="me-1" />}
                  {label}
                </Button>
              ))}
            </div>
          </Form.Group>

          {/* OUTCOME FILTER */}
          <Form.Group>
            <Form.Label className="fw-semibold">Call Outcome</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { key: "all", label: "All Outcomes" },
                { key: "qualified", label: "Qualified" },
                { key: "unqualified", label: "Unqualified" }
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  variant={outcome === key ? "secondary" : "outline-secondary"}
                  size="sm"
                  onClick={() => {
                    setPage(1);
                    setOutcome(key);
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* CONTENT AREA */}
      <div className="position-relative">
        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
            <LoadingSpinner text="Loading calls..." />
          </div>
        )}

        {/* =======================
            MOBILE – CARDS
        ======================= */}
      {isMobile ? (
        calls.map((c) => {
          const expanded = expandedId === c.id;

          return (
            <Card key={c.id} className="mb-3 shadow-sm hover-lift">
              <Card.Body
                onClick={() => setExpandedId(expanded ? null : c.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(expanded ? null : c.id);
                  }
                }}
                className="p-3"
                style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                tabIndex={0}
                role="button"
                aria-expanded={expanded}
                aria-label={`Call from ${c.name || 'Unknown'} - ${expanded ? 'Collapse' : 'Expand'} details`}
              >
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h6 className="mb-0 me-2">{c.name || "Unknown Caller"}</h6>
                      {c.is_existing_lead ? (
                        <Badge bg="info" className="small">
                          <FaUserCheck className="me-1" />
                          Existing
                        </Badge>
                      ) : (
                        <Badge bg="primary" className="small">
                          <FaUserPlus className="me-1" />
                          New
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted small">{c.caller_number}</div>
                  </div>
                  <Badge bg={badgeVariant(c.outcome)} className="ms-2">
                    {c.outcome || "Unknown"}
                  </Badge>
                </div>

                {/* Issue */}
                <div className="mb-2">
                  <strong className="text-primary">Issue:</strong> {c.issue || "Not specified"}
                </div>

                {/* Summary (always visible) */}
                <div className="mb-2">
                  <strong>Summary:</strong>
                  <div className="text-muted small mt-1">
                    {c.summary || "No summary available"}
                  </div>
                </div>

                {/* Expand indicator */}
                <div className="text-center text-muted mt-2">
                  <small>
                    {expanded ? (
                      <>
                        <FaChevronUp className="me-1" />
                        Tap to collapse
                      </>
                    ) : (
                      <>
                        <FaChevronDown className="me-1" />
                        Tap for details
                      </>
                    )}
                  </small>
                </div>

                {expanded && (
                  <div className="mt-3 pt-3 border-top">
                    <div className="row g-2">
                      <div className="col-6">
                        <strong className="small text-muted d-block">Address</strong>
                        <div className="small">{c.address || "Not provided"}</div>
                      </div>
                      <div className="col-6">
                        <strong className="small text-muted d-block">Duration</strong>
                        <div className="small">{c.duration_secs || 0}s</div>
                      </div>
                    </div>

                    {/* Proposal Status (Existing Only) */}
                    {c.is_existing_lead && (
                      <div className="mt-3">
                        <strong className="small text-muted d-block">Proposal Status</strong>
                        {proposalBadge(c.proposal_status)}
                      </div>
                    )}

                    {/* Transcript */}
                    {c.transcript && (
                      <div className="mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setSelectedTranscript({ transcript: c.transcript, name: c.name });
                            setShowTranscriptModal(true);
                          }}
                        >
                          Show Full Transcript
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })
      ) : (
        /* =======================
            DESKTOP – TABLE
        ======================= */
        <Card className="shadow-sm">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="border-0 fw-semibold">Caller</th>
                  <th className="border-0 fw-semibold">Lead Type</th>
                  <th className="border-0 fw-semibold">Outcome</th>
                  <th className="border-0 fw-semibold">Phone</th>
                  <th className="border-0 fw-semibold">Issue</th>
                  <th className="border-0 fw-semibold">Agent</th>
                  <th className="border-0 fw-semibold">Call Time</th>
                  <th className="border-0 fw-semibold text-center">Details</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr
                      onClick={() =>
                        setExpandedId(expandedId === c.id ? null : c.id)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedId(expandedId === c.id ? null : c.id);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                      className="align-middle"
                      tabIndex={0}
                      role="button"
                      aria-expanded={expandedId === c.id}
                      aria-label={`Call from ${c.name || 'Unknown'} - ${expandedId === c.id ? 'Collapse' : 'Expand'} details`}
                    >
                      <td>
                        <div className="fw-semibold">{c.name || "Unknown"}</div>
                        <div className="text-muted small">{c.caller_number}</div>
                      </td>
                      <td>
                        {c.is_existing_lead ? (
                          <Badge bg="info" className="d-flex align-items-center justify-content-center">
                            <FaUserCheck className="me-1" />
                            Existing
                          </Badge>
                        ) : (
                          <Badge bg="primary" className="d-flex align-items-center justify-content-center">
                            <FaUserPlus className="me-1" />
                            New
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg={badgeVariant(c.outcome)} className="fs-6">
                          {c.outcome || "Unknown"}
                        </Badge>
                      </td>
                      <td className="font-monospace">{c.caller_number}</td>
                      <td className="text-truncate" style={{ maxWidth: "200px" }} title={c.issue}>
                        {c.issue || "Not specified"}
                      </td>
                      <td>{c.agent_name || "Unknown"}</td>
                      <td className="small">
                        {new Date(c.call_time).toLocaleDateString()}
                        <br />
                        <span className="text-muted">
                          {new Date(c.call_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="text-center">
                        <Button variant="link" size="sm" className="p-0 text-decoration-none">
                          {expandedId === c.id ? (
                            <FaChevronUp className="text-primary" />
                          ) : (
                            <FaChevronDown className="text-muted" />
                          )}
                        </Button>
                      </td>
                    </tr>

                    {expandedId === c.id && (
                      <tr className="bg-light">
                        <td colSpan={8} className="border-0">
                          <Card className="border-0 shadow-none bg-transparent">
                            <Card.Body className="p-3">
                              <div className="row">
                                <div className="col-md-8">
                                  <h6 className="text-primary mb-2">Call Summary</h6>
                                  <p className="mb-3">{c.summary || "No summary available"}</p>

                                  {c.is_existing_lead && (
                                    <div className="mb-3">
                                      <strong>Proposal Status:</strong>
                                      <span className="ms-2">{proposalBadge(c.proposal_status)}</span>
                                    </div>
                                  )}

                                  <div className="row g-3">
                                    <div className="col-sm-6">
                                      <strong className="small text-muted d-block">Address</strong>
                                      <div>{c.address || "Not provided"}</div>
                                    </div>
                                    <div className="col-sm-6">
                                      <strong className="small text-muted d-block">Duration</strong>
                                      <div>{c.duration_secs || 0} seconds</div>
                                    </div>
                                  </div>
                                </div>

                                {c.transcript && (
                                  <div className="col-md-4">
                                    <h6 className="text-primary mb-2">Transcript</h6>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedTranscript({ transcript: c.transcript, name: c.name });
                                        setShowTranscriptModal(true);
                                      }}
                                    >
                                      Show Full Transcript
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* PAGINATION */}
      {totalPages > 1 && (
        <Card className="mt-4 shadow-sm">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Showing {calls.length} of {count} calls
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(page - 1)}
                >
                  ← Previous
                </Button>

                <div className="d-flex align-items-center gap-1">
                  <span className="small text-muted">Page</span>
                  <Form.Control
                    type="number"
                    min={1}
                    max={totalPages}
                    value={page}
                    onChange={(e) => {
                      const newPage = parseInt(e.target.value);
                      if (newPage >= 1 && newPage <= totalPages) {
                        setPage(newPage);
                      }
                    }}
                    className="text-center"
                    style={{ width: "60px" }}
                    size="sm"
                  />
                  <span className="small text-muted">of {totalPages}</span>
                </div>

                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage(page + 1)}
                >
                  Next →
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* EMPTY STATE */}
      {calls.length === 0 && !loading && (
        <Card className="mt-4 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaSearch size={48} className="text-muted mb-3" />
            <h5 className="text-muted">No calls found</h5>
            <p className="text-muted mb-0">
              Try adjusting your search criteria or filters to find more calls.
            </p>
          </Card.Body>
        </Card>
      )}
      </div>

      {/* TRANSCRIPT MODAL */}
      <Modal show={showTranscriptModal} onHide={() => setShowTranscriptModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Call Transcript</Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(selectedTranscript.transcript);
              toast.success('Transcript copied to clipboard');
            }}
            className="ms-2"
          >
            <FaCopy className="me-1" />
            Copy
          </Button>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: "400px", overflow: "auto" }}>
            {selectedTranscript.transcript.split('\n').map((line, index) => {
              const isAgent = line.startsWith('Agent:');
              const isCaller = line.startsWith('Caller:');
              const cleanLine = line.replace(/^(Agent:|Caller:)\s*/, '');
              const otherLabel = selectedTranscript.name || 'Customer';
              return (
                <div
                  key={index}
                  style={{
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    backgroundColor: isAgent ? '#e3f2fd' : isCaller ? '#f3e5f5' : 'transparent',
                    border: !isAgent && !isCaller ? '1px solid #dee2e6' : 'none',
                  }}
                >
                  {isAgent && <strong className="text-primary">Agent:</strong>}
                  {isCaller && <strong className="text-success">{otherLabel}:</strong>}
                  {!isAgent && !isCaller && <strong>{otherLabel}:</strong>}
                  {' '}{cleanLine}
                </div>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default InboundCallsPage;
